import { useEffect, useRef, type ReactNode } from 'react';
import { Popup } from './Popup';
import './WinPopup.css';

interface WinPopupProps {
  title: string;
  solution: boolean[][];
  children?: ReactNode;
  buttons: ReactNode;
  /** When provided, renders a top-right X button that invokes this callback. */
  onClose?: () => void;
}

/* Mini solution grid — same visual style as the game board canvas */
function SolutionCanvas({ solution }: { solution: boolean[][] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rows = solution.length;
  const cols = solution[0]?.length ?? rows;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cw = w / cols;
    const ch = h / rows;
    const cornerR = Math.min(cw, ch) * 0.14;

    // Clear (transparent background)
    ctx.clearRect(0, 0, w, h);

    // Cells
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * cw;
        const y = r * ch;
        // Corner rounding for the four board corners
        const tl = (r === 0 && c === 0) ? cornerR : 0;
        const tr = (r === 0 && c === cols - 1) ? cornerR : 0;
        const bl = (r === rows - 1 && c === 0) ? cornerR : 0;
        const br = (r === rows - 1 && c === cols - 1) ? cornerR : 0;

        ctx.beginPath();
        ctx.moveTo(x + tl, y);
        ctx.lineTo(x + cw - tr, y);
        if (tr) ctx.quadraticCurveTo(x + cw, y, x + cw, y + tr); else ctx.lineTo(x + cw, y);
        ctx.lineTo(x + cw, y + ch - br);
        if (br) ctx.quadraticCurveTo(x + cw, y + ch, x + cw - br, y + ch); else ctx.lineTo(x + cw, y + ch);
        ctx.lineTo(x + bl, y + ch);
        if (bl) ctx.quadraticCurveTo(x, y + ch, x, y + ch - bl); else ctx.lineTo(x, y + ch);
        ctx.lineTo(x, y + tl);
        if (tl) ctx.quadraticCurveTo(x, y, x + tl, y); else ctx.lineTo(x, y);
        ctx.closePath();

        if (solution[r][c]) {
          ctx.fillStyle = '#f0b830';
          ctx.fill();
        }
      }
    }

    // Grid lines (same as BoardGrid)
    const lineW = Math.max(1, cw * 0.024);
    ctx.fillStyle = '#c8b898';
    for (let ri = 1; ri < rows; ri++) {
      ctx.fillRect(0, ri * ch - lineW / 2, w, lineW);
    }
    for (let ci = 1; ci < cols; ci++) {
      ctx.fillRect(ci * cw - lineW / 2, 0, lineW, h);
    }

    // 5-cell separator lines for larger grids
    if (rows > 5 || cols > 5) {
      const sepW = Math.max(1.5, cw * 0.06);
      ctx.fillStyle = '#a89060';
      for (let ri = 5; ri < rows; ri += 5) {
        ctx.fillRect(0, ri * ch - sepW / 2, w, sepW);
      }
      for (let ci = 5; ci < cols; ci += 5) {
        ctx.fillRect(ci * cw - sepW / 2, 0, sepW, h);
      }
    }
  }, [solution, rows, cols]);

  return (
    <canvas
      ref={canvasRef}
      className="win-popup-canvas"
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
}

export function WinPopup({ title, solution, children, buttons, onClose }: WinPopupProps) {
  const rows = solution.length;
  const cols = solution[0]?.length ?? rows;

  return (
    <Popup aria-label={title} onDismiss={onClose} maxWidth={360}>
      <Popup.Header title={title} onClose={onClose} className="win-popup-header" />
      <Popup.Body>
        <div
          className="win-popup-grid"
          style={{ aspectRatio: `${cols} / ${rows}` }}
        >
          <SolutionCanvas solution={solution} />
        </div>
        {children}
      </Popup.Body>
      <Popup.Footer>{buttons}</Popup.Footer>
    </Popup>
  );
}
